// AI 実行履歴画面
import { escapeHtml, screenHead, showScreen, toast } from '../ui.js';
import { getAllAiHistory, deleteAiHistory } from '../cache.js';

const TYPE_LABELS = {
  'recommend': 'おすすめの本',
  'keyword-search': 'キーワードから探す',
  'contrast': '対立軸の書籍',
  'serendipity': 'セレンディピティ',
  'map': '思想マップ',
  'lineage': '系譜・影響関係',
  'course': '読書合宿プラン',
  'journal': '月次思想ジャーナル',
  'analyze': '哲学者の視座で分析',
};

const TYPE_ICONS = {
  'recommend': 'i-sparkles',
  'keyword-search': 'i-key',
  'contrast': 'i-divide',
  'serendipity': 'i-compass',
  'map': 'i-map',
  'lineage': 'i-network',
  'course': 'i-route',
  'journal': 'i-quote',
  'analyze': 'i-eye',
};

export async function showHistoryScreen() {
  const items = await getAllAiHistory();
  if (!items.length) {
    showScreen('history', `${screenHead('実行履歴')}<p class="empty">まだ AI 機能の実行履歴がありません。<br/>各機能を実行すると、ここに自動で記録されます。</p>`);
    document.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }
  // タイプ別フィルタ用に全タイプを抽出
  const types = ['すべて', ...Array.from(new Set(items.map((x) => x.type)))];
  const html = `
    ${screenHead('実行履歴')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      ${items.length} 件の履歴。タップで結果を再表示できます。
    </p>
    <div id="hist-tabs" class="genre-tabs">
      ${types
        .map((t) => `<button class="genre-tab${t === 'すべて' ? ' genre-tab--active' : ''}" data-type="${escapeHtml(t)}" type="button">${escapeHtml(t === 'すべて' ? 'すべて' : TYPE_LABELS[t] || t)}</button>`)
        .join('')}
    </div>
    <ul id="hist-list" class="book-list" aria-live="polite"></ul>
  `;
  const screen = showScreen('history', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());

  let activeType = 'すべて';

  function renderList() {
    const filtered = activeType === 'すべて' ? items : items.filter((x) => x.type === activeType);
    const ul = screen.querySelector('#hist-list');
    ul.innerHTML = filtered
      .map((it) => {
        const icon = TYPE_ICONS[it.type] || 'i-sparkles';
        const label = TYPE_LABELS[it.type] || it.type;
        const dateStr = new Date(it.createdAt).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        return `
        <li class="book-item" data-hist-id="${escapeHtml(it.id)}">
          <div style="width:36px;height:36px;flex-shrink:0;display:flex;align-items:center;justify-content:center;border:1px solid var(--line);border-radius:50%;">
            <svg class="icon"><use href="#${icon}"/></svg>
          </div>
          <div class="book-body">
            <p class="book-author" style="font-family:var(--font-mono);letter-spacing:0.12em;font-size:10px;text-transform:uppercase;color:var(--mute);margin-bottom:2px;">${escapeHtml(label)} · ${escapeHtml(dateStr)}</p>
            <p class="book-title" style="font-size:14px;">${escapeHtml(it.title || '')}</p>
            ${it.subtitle ? `<p class="book-author" style="margin-top:2px;">${escapeHtml(it.subtitle)}</p>` : ''}
          </div>
          <button class="btn btn--icon" data-delete-hist="${escapeHtml(it.id)}" type="button" aria-label="削除">
            <svg class="icon" style="width:14px;height:14px;color:var(--mute);"><use href="#i-close"/></svg>
          </button>
        </li>
      `;
      })
      .join('');
    ul.querySelectorAll('[data-hist-id]').forEach((li) => {
      li.addEventListener('click', async (e) => {
        if (e.target.closest('[data-delete-hist]')) return;
        const id = parseInt(li.dataset.histId, 10);
        const entry = items.find((x) => x.id === id);
        if (entry) replayHistory(entry);
      });
    });
    ul.querySelectorAll('[data-delete-hist]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = parseInt(btn.dataset.deleteHist, 10);
        if (!confirm('この履歴を削除しますか？')) return;
        await deleteAiHistory(id);
        const idx = items.findIndex((x) => x.id === id);
        if (idx >= 0) items.splice(idx, 1);
        renderList();
        toast('削除しました');
      });
    });
  }

  screen.querySelectorAll('[data-type]').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeType = btn.dataset.type;
      screen.querySelectorAll('[data-type]').forEach((b) => b.classList.remove('genre-tab--active'));
      btn.classList.add('genre-tab--active');
      renderList();
    });
  });
  renderList();
}

async function replayHistory(entry) {
  // type に応じて該当機能の renderFromHistory を呼ぶ
  try {
    switch (entry.type) {
      case 'recommend':
        (await import('./recommend.js')).renderFromHistory(entry);
        break;
      case 'keyword-search':
        (await import('./keyword-search.js')).renderFromHistory(entry);
        break;
      case 'contrast':
        (await import('./contrast.js')).renderFromHistory(entry);
        break;
      case 'serendipity':
        (await import('./serendipity.js')).renderFromHistory(entry);
        break;
      case 'map':
        (await import('./thought-map.js')).renderFromHistory(entry);
        break;
      case 'lineage':
        (await import('./lineage.js')).renderFromHistory(entry);
        break;
      case 'course':
        (await import('./reading-course.js')).renderFromHistory(entry);
        break;
      case 'journal':
        (await import('./journal.js')).renderFromHistory(entry);
        break;
      case 'analyze':
        (await import('./analyze.js')).renderFromHistory(entry);
        break;
      default:
        toast('未対応の履歴タイプです');
    }
  } catch (e) {
    console.error('replay error', e);
    toast('再表示に失敗しました');
  }
}
